import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { map, tap, pluck, catchError, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { combineLatest } from 'rxjs/observable/combineLatest';

import {
  Approval,
  ApprovalProduct,
  ApprovalPrivacyConsents,
  ApprovalGenericConsents,
  ApprovalConsents,
  ApprovalResponse,
  ApprovalFeedback,
  ApprovalLinearResponse,
  ApprovalLinearRequest,
  ApprovalAgileRequest,
  ApprovalPromoNameAndDiscount,
} from './alg-consenso.service.d';
import { environment } from '../../environments/environment';
import { Promotion } from '../models/promotion';
import { Autoricarica, CambioPiano, Fisso } from '../models/fisso';
import { BillingFrequency } from '../models/billingFrequency';
import { VulnerabilityType } from '../models/product';
import { DocsReception } from '../models/docsReception';
import { SupplyInvoiceReception } from '../models/supplyInvoiceReception';
import { PaymentMethod } from '../models/paymentMethod';

export const APPROVAL_STATUS_OK = 'approval_status_ok';
export const APPROVAL_STATUS_COMPLETED = 'approval_status_completed';
export const APPROVAL_STATUS_SIGNED = 'approval_status_signed';
export const APPROVAL_STATUS_INVALID = 'approval_status_invalid';
export const APPROVAL_STATUS_ERROR = 'approval_status_error';
export const REQUEST_STATUS_OK = 'request_status_OK';
export const REQUEST_STATUS_INVALID = 'request_status_invalid';
export const REQUEST_STATUS_CANCELED = 'Request canceled';

const defaultPrivacyConsents = {
  valid: false,
  data: {
    privacyComCom: false,
    privacyProf: false,
    privacyQualServ: false,
    privacyComTer: false,
    privacyProfTer: false,
    generalPrivacy: false,
    docsReception: false,
  },
};

const defaultGenericConsents = {
  valid: false,
  data: {
    cteCondition: false,
    cfgCondition: false,
    cadastralCondition: false,
    clauseCondition: false,
    consentsCondition: false,
  },
};

@Injectable()
export class AlgConsensoService implements OnDestroy {
  public otpVerified$ = new BehaviorSubject<boolean>(false);
  public approval$: Observable<Approval>;
  public linearRequest$: Observable<any>;
  public changePlanRequest$: Observable<any>;
  public billingFrequencyRequest$: Observable<any>;
  public docsReceptionRequest$: Observable<any>;
  public supplyInvoiceReceptionRequest$: Observable<any>;
  public paymentMethodRequest$: Observable<any>;
  public autoricaricaRequest$: Observable<any>;
  public autoricaricaSupplyRequest$: Observable<any>;
  public agileProduct$: Observable<any>;
  public agileRequest$: Observable<any>;
  public approvalProduct$: Observable<ApprovalProduct>;
  public approvalProductName$: Observable<string>;
  public approvalStatus$: Observable<string>;
  public linearRequestStatus$: Observable<string>;
  public changePlanRequestStatus$: Observable<string>;
  public billingFrequencyRequestStatus$: Observable<string>;
  public docsReceptionRequestStatus$: Observable<string>;
  public supplyInvoiceReceptionRequestStatus$: Observable<string>;
  public paymentMethodRequestStatus$: Observable<string>;
  public autoricaricaRequestStatus$: Observable<string>;
  public autoricaricaInfoSuppliesStatus$: Observable<string>;
  public agileRequestStatus$: Observable<string>;
  public fissoRequestStatus$: Observable<string>;
  public fissoRequestResult$: Observable<any>;
  public isLoading$: Observable<boolean>;
  public isFeedbackLoading$: Observable<boolean>;
  public isConsentsOk$: Observable<boolean>;
  public privacyConsets$: Observable<ApprovalPrivacyConsents>;
  public genericConsets$: Observable<ApprovalGenericConsents>;
  public policiesUrl$: Observable<any>;
  public categoryNumber$: Observable<number>;
  public operatorRatingCode$: Observable<ApprovalFeedback>;
  public promoNameAndDiscountValidity$: Observable<ApprovalPromoNameAndDiscount>;
  public gasLinearSubject$ = new BehaviorSubject<boolean>(false);
  public gasLinearInformative$ = new BehaviorSubject<boolean>(false);
  public lightLinearSubject$ = new BehaviorSubject<boolean>(false);
  public lightLinearInformative$ = new BehaviorSubject<boolean>(false);
  public isOtpAgileRequest$ = new BehaviorSubject<boolean>(false);
  public isOtpBillingFrequencyRequest$ = new BehaviorSubject<boolean>(false);
  public isOtpDocsReceptionRequest$ = new BehaviorSubject<boolean>(false);
  public isOtpSupplyInvoiceReceptionRequest$ = new BehaviorSubject<boolean>(false);
  public isOtpPaymentMethodRequest$ = new BehaviorSubject<boolean>(false);
  public isSecontStepFissoRequest$ = new BehaviorSubject<boolean>(false);
  public isSecondStepAutoricarica$ = new BehaviorSubject<boolean>(false);
  public checkbox104Checked$ = new BehaviorSubject<boolean>(false);
  public checkboxDocsReception$ = new BehaviorSubject<boolean>(false);
  public checkboxSupplyStart$ = new BehaviorSubject<boolean>(false);
  public checkboxGeneralCondition$ = new BehaviorSubject<boolean>(false);
  public uploaded104DocumentModule$ = new BehaviorSubject<boolean>(false);
  public uploaded104DocumentFront$ = new BehaviorSubject<boolean>(false);
  public uploaded104DocumentBack$ = new BehaviorSubject<boolean>(false);
  public paymentConsentSubject$ = new BehaviorSubject<boolean>(null);
  public otpTimeCheck$ = new ReplaySubject<any>(1);
  public privacyConsentsSubject = new BehaviorSubject<{
    valid: boolean;
    data: ApprovalPrivacyConsents;
  }>(defaultPrivacyConsents);
  public genericConsentsSubject = new BehaviorSubject<{
    valid: boolean;
    data: ApprovalGenericConsents;
  }>(defaultGenericConsents);
  public promotions: BehaviorSubject<Promotion[]> = new BehaviorSubject<Promotion[]>([] as Promotion[]);
  public vulnerabilityTypeUserSelected$ = new BehaviorSubject<VulnerabilityType>(VulnerabilityType.anagraficAge);
  public isVulnerabilityTypeDisability$ = new BehaviorSubject<boolean>(false);
  public vulnerabilityType$ = new BehaviorSubject<VulnerabilityType>(VulnerabilityType.anagraficAge);
  private approvalResponseSubject = new ReplaySubject<ApprovalResponse>(1);
  private approvalLinearResponseSubject = new ReplaySubject<any>(1);
  private approvalChangePlanResponseSubject = new ReplaySubject<any>(1);
  private billingFrequencyResponseSubject = new ReplaySubject<any>(1);
  private docsReceptionResponseSubject = new ReplaySubject<any>(1);
  private supplyInvoiceReceptionResponseSubject = new ReplaySubject<any>(1);
  private paymentMethodResponseSubject = new ReplaySubject<any>(1);
  private autoricaricaResponseSubject = new ReplaySubject<any>(1);
  private autoricaricaUploadDocumentsResponseSubject = new ReplaySubject<any>(1);
  private autoricaricaSuppliesResponseSubject = new ReplaySubject<any>(1);
  private promoNameResponseSubject = new ReplaySubject<any>(1);
  private approvalAgileResponseSubject = new ReplaySubject<any>(1);
  private approvalFissoResponseSubject = new ReplaySubject<any>(1);
  private fissoResponseSubject = new ReplaySubject<any>(1);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private feedbackLoadingSubject = new BehaviorSubject<boolean>(false);
  private ngUnsubscribe = new Subject<void>();
  private approvalsEndpoint = `${environment.api.basePath}/approvals`;
  private opportunitiesEndpoint = `${environment.api.basePath}/opportunities`;
  private checkOpportunitiesEndpoint = `${environment.api.basePath}/check-opportunity`;
  private linearRequest = `${environment.api.basePath}/requests`;
  private changePlanRequest = `${environment.api.basePath}/change-plan`;
  private billingFrequencyRequest = `${environment.api.basePath}/change-billing-frequency`;
  private docsReceptionRequest = `${environment.api.basePath}/docs-reception`;
  private supplyInvoiceReceptionRequest = `${environment.api.basePath}/supply-invoice-reception`;
  private paymentMethodRequest = `${environment.api.basePath}/payment-method`;
  private confirmDocsReceptionRequest = `${environment.api.basePath}/docs-reception-request`;
  private confirmSupplyInvoiceReceptionRequest = `${environment.api.basePath}/supply-invoice-reception-request`;
  private confirmPaymentMethodRequest = `${environment.api.basePath}/payment-method-request`;
  private autoRicaricaRequest = `${environment.api.basePath}/autoricarica`;
  private agileRequest = `${environment.api.basePath}/agile-requests`;
  private fissoRequest = `${environment.api.basePath}/fisso-requests`;
  private sendChangePlanRequest = `${environment.api.basePath}/change-plan-request`;
  private sendAutoricaricaRequest = `${environment.api.basePath}/autoricarica-request`;
  private fissoContractIdStatus = `${environment.api.basePath}/contract-fisso-status`;
  private promoNameEndpoint = `${environment.api.basePath}/promo-name`;
  private uploadAutoricaricaPDF = `${environment.api.basePath}/upload-autoricarica-pdf`;

  // private approvalsEndpoint = `${'http://localhost:5000/api'}/approvals`;
  // private opportunitiesEndpoint = `${'http://localhost:5000/api'}/opportunities`;
  // private checkOpportunitiesEndpoint = `${'http://localhost:5000/api'}/check-opportunity`;

  // consenso status check
  constructor(private http: HttpClient) {
    this.approvalStatus$ = this.approvalResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const approval = responseOk && !!response && response.approval;
        const approvalStatus = !!approval && !!approval.opportunityStatus && approval.opportunityStatus.toLowerCase();
        const completed = approval && approval.completed;
        switch (true) {
          case (approvalStatus === 'contract sent' || approvalStatus === 'contract queried' || approvalStatus === 'capture order details') && completed:
            return APPROVAL_STATUS_COMPLETED;
          case (approvalStatus === 'contract sent' || approvalStatus === 'contract queried' || approvalStatus === 'capture order details') && !completed:
            return APPROVAL_STATUS_OK;
          case approvalStatus === 'contract signed' || approvalStatus === 'contratto da verificare':
            return APPROVAL_STATUS_SIGNED;
          case approvalStatus === 'contract lost' || approvalStatus === 'closed lost':
            return APPROVAL_STATUS_INVALID;
          default:
            return APPROVAL_STATUS_ERROR;
        }
      })
    );

    // Linear status check
    this.linearRequestStatus$ = this.approvalLinearResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseLinear = responseOk && response.status;
        switch (true) {
          case responseLinear === 200:
            return REQUEST_STATUS_OK;
          case responseLinear !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    // Cambio piano status check
    this.changePlanRequestStatus$ = this.approvalChangePlanResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseChangePlan = responseOk && response.status;
        switch (true) {
          case responseChangePlan === 200:
            return REQUEST_STATUS_OK;
          case responseChangePlan !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    // Cambio frequenza fatturazione status check
    this.billingFrequencyRequestStatus$ = this.billingFrequencyResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseBillingFrequency = responseOk && response.status;

        switch (true) {
          case responseBillingFrequency === 200:
            return REQUEST_STATUS_OK;
          case responseBillingFrequency !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    this.docsReceptionRequestStatus$ = this.docsReceptionResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseDocsReception = responseOk && response.status;
        const requestCanceled = response.request && response.request.requestStatus === REQUEST_STATUS_CANCELED;
        switch (true) {
          case requestCanceled:
            return REQUEST_STATUS_CANCELED;
          case responseDocsReception === 200:
            return REQUEST_STATUS_OK;
          case responseDocsReception !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );
    this.supplyInvoiceReceptionRequestStatus$ = this.supplyInvoiceReceptionResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseSupplyInvoiceReception = responseOk && response.status;
        console.log('responseSupplyInvoiceReception', response);
        const requestCanceled = response.request && response.request.requestStatus === REQUEST_STATUS_CANCELED;
        switch (true) {
          case requestCanceled:
            return REQUEST_STATUS_CANCELED;
          case responseSupplyInvoiceReception === 200:
            return REQUEST_STATUS_OK;
          case responseSupplyInvoiceReception !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    this.paymentMethodRequestStatus$ = this.paymentMethodResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responsePaymentMethod = responseOk && response.status;
        const requestCanceled = response.request && response.request.requestStatus === REQUEST_STATUS_CANCELED;
        switch (true) {
          case requestCanceled:
            return REQUEST_STATUS_CANCELED;
          case responsePaymentMethod === 200:
            return REQUEST_STATUS_OK;
          case responsePaymentMethod !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    // Autoricarica status check
    this.autoricaricaRequestStatus$ = this.autoricaricaResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseAutoricarica = responseOk && response.status;
        switch (true) {
          case responseAutoricarica === 200:
            return REQUEST_STATUS_OK;
          case responseAutoricarica !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    // Autoricarica Supplies check
    this.autoricaricaInfoSuppliesStatus$ = this.autoricaricaSuppliesResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseAutoricarica = responseOk && response.status;
        switch (true) {
          case responseAutoricarica === 200:
            return REQUEST_STATUS_OK;
          case responseAutoricarica !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    // Agile status check
    this.agileRequestStatus$ = this.approvalAgileResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseAgile = responseOk && response.status;
        switch (true) {
          case responseAgile === 200:
            return REQUEST_STATUS_OK;
          case responseAgile !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    this.fissoRequestStatus$ = this.approvalFissoResponseSubject.pipe(
      map((response) => {
        const responseOk = !!response && Math.ceil(response.status / 100) === 2;
        const responseFisso = responseOk && response.status;
        switch (true) {
          case responseFisso === 200:
            return REQUEST_STATUS_OK;
          case responseFisso !== 200:
            return REQUEST_STATUS_INVALID;
        }
      })
    );

    this.isLoading$ = this.loadingSubject.pipe(distinctUntilChanged());
    this.isFeedbackLoading$ = this.feedbackLoadingSubject.pipe(distinctUntilChanged());
    this.approval$ = this.approvalResponseSubject.pipe(map((response) => (!!response && response.approval) || null));
    this.linearRequest$ = this.approvalLinearResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.changePlanRequest$ = this.approvalChangePlanResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.billingFrequencyRequest$ = this.billingFrequencyResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.docsReceptionRequest$ = this.docsReceptionResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.supplyInvoiceReceptionRequest$ = this.supplyInvoiceReceptionResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.paymentMethodRequest$ = this.paymentMethodResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.autoricaricaRequest$ = this.autoricaricaResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.autoricaricaSupplyRequest$ = this.autoricaricaSuppliesResponseSubject.pipe(map((response) => (!!response && response.request) || null));

    this.agileRequest$ = this.approvalAgileResponseSubject.pipe(map((response) => (!!response && response.request) || null));
    this.approvalProduct$ = this.approval$.pipe(map((approval) => (!!approval && approval.product) || null));
    this.agileProduct$ = this.agileRequest$.pipe(
      map((agile) => {
        (!!agile && agile.product) || null;
      })
    );
    this.fissoRequestResult$ = this.fissoResponseSubject.pipe(map((response) => !!response && response.result));
    this.approvalProductName$ = this.approvalProduct$.pipe(
      map((product) => !!product && `${product.family || ''} ${product.type || ''} ${product.duration || ''} ${product.lightRate || ''}`)
    );
    this.promoNameAndDiscountValidity$ = this.promoNameResponseSubject.pipe(map((response) => (!!response && response.request) || null));

    this.isConsentsOk$ = combineLatest(
      this.privacyConsentsSubject.pipe(map((consentStatus) => consentStatus.valid)),
      this.genericConsentsSubject.pipe(map((consentStatus) => consentStatus.valid))
    ).pipe(
      map(([privacyConsentsValid, genericConsentsValid]) => privacyConsentsValid && genericConsentsValid)
      // distinctUntilChanged()
    );

    this.privacyConsets$ = this.privacyConsentsSubject.pipe(pluck('data'));
    this.genericConsets$ = this.genericConsentsSubject.pipe(pluck('data'));
    this.operatorRatingCode$ = this.approval$.pipe(map((approval) => (!!approval && approval.operatorRatingCode) || null));
    this.policiesUrl$ = this.approval$.pipe(
      map((approval) => ({
        cfgCondition: (!!approval && approval.cfgPolicyUrl) || '',
        cteCondition: (!!approval && approval.ctePolicyUrl) || '',
        generalPolicy: (!!approval && approval.generalPolicyUrl) || '',
      }))
    );
    this.categoryNumber$ = this.approval$.pipe(map((approval) => (!!approval && approval.categoryNumber) || null));
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public checkOpportunity(id: string): any {
    return this.http.get(`${this.checkOpportunitiesEndpoint}/${id}`, { observe: 'response' }).pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        result: response.body,
      })),
      catchError((response: HttpResponse<any>) =>
        Observable.of({
          status: response.status,
          result: response.statusText,
        })
      )
    );
  }

  public fetchOpportunity(id: string): void {
    this.loadingSubject.next(true);
    this.getOpportunity(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalResponseSubject.next(response);
      });
  }

  public fetchNamePromoOpportuniy(id: string): void {
    this.loadingSubject.next(true);
    this.getPromoNameOpportunity(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.promoNameResponseSubject.next(response);
      });
  }

  public fetchLinearRequest(id: string, codice: string, tipo: string): void {
    this.loadingSubject.next(true);
    this.getLinearRequest(id, codice, tipo)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalLinearResponseSubject.next(response);
      });
  }

  public fetchChangePlanRequest(id: string, codice: string, tipo: string): void {
    this.loadingSubject.next(true);
    this.getChangePlanRequest(id, codice, tipo)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalChangePlanResponseSubject.next(response);
      });
  }

  public fetchBillingFrequency(id: string, codice: string, tipo: string): void {
    this.loadingSubject.next(true);
    this.getBillingFrequency(id, codice, tipo)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.billingFrequencyResponseSubject.next(response);
      });
  }
  public fetchDocsReception(id: string): void {
    this.loadingSubject.next(true);
    this.getDocsReception(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.docsReceptionResponseSubject.next(response);
      });
  }

  public fetchSupplyInvoiceReception(id: string): void {
    this.loadingSubject.next(true);
    this.getSupplyInvoiceReception(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.supplyInvoiceReceptionResponseSubject.next(response);
      });
  }

  public fetchPaymentMethodRequest(id: string): void {
    this.loadingSubject.next(true);
    this.getPaymentMethodRequest(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.paymentMethodResponseSubject.next(response);
      });
  }

  public fetchAutoricarica(data: any): void {
    this.loadingSubject.next(true);
    this.getAutoRicaricaInfos(data)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        if (response.status === 500) {
          this.autoricaricaResponseSubject.next({
            status: 500,
            request: 'ID Not found',
          });
        }
        this.autoricaricaResponseSubject.next(response);
      });
  }

  public fetchAutoricaricaSupplies(supplies: any): void {
    this.loadingSubject.next(true);
    this.getAutoRicaricaSuppliesInfo(supplies)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        if (response.status === 500) {
          this.autoricaricaSuppliesResponseSubject.next({
            status: 500,
            request: 'Supplies Not found',
          });
        }
        console.log('fetchAutoricaricaSupplies', response);
        this.autoricaricaSuppliesResponseSubject.next(response);
      });
  }

  public fetchAgileRequest(id): void {
    this.loadingSubject.next(true);
    this.getAgileRequest(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalAgileResponseSubject.next(response);
      });
  }

  public fetchOpportunityUnipol(id: string, unipol: boolean | null): void {
    this.loadingSubject.next(true);
    this.getOpportunityUnipol(id, unipol)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalResponseSubject.next(response);
      });
  }

  public saveConsents(id, consents: ApprovalConsents, complete: boolean = false) {
    // Set function field to enable Salesforce to compute the opportunity
    this.loadingSubject.next(true);
    this.saveOpportunity(id, {
      ...consents,
      // NOTE: De-Commenting it makes an opportunity ID not usable anymore
      function: 'SMART',
    })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalResponseSubject.next(response);
      });
  }

  public saveConsentsUnipol(id, consents: ApprovalConsents, complete: boolean = false) {
    // Set function field to enable Salesforce to compute the opportunity
    this.loadingSubject.next(true);
    this.saveOpportunityUnipol(id, {
      ...consents,
      // NOTE: De-Commenting it makes an opportunity ID not usable anymore
      function: 'SMART',
    })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalResponseSubject.next(response);
      });
  }

  public saveLinearRequest(consents: ApprovalLinearRequest) {
    // Set function field to enable Salesforce to compute the opportunity
    this.loadingSubject.next(true);
    this.saveNewLinearRequest({
      ...consents,
      // NOTE: De-Commenting it makes an opportunity ID not usable anymore
      // function: 'SMART',
    })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalLinearResponseSubject.next(response);
      });
  }

  public saveAgileRequest(consents: ApprovalAgileRequest) {
    // Set function field to enable Salesforce to compute the opportunity
    this.loadingSubject.next(true);
    this.saveNewAgileRequest({
      ...consents,
      // NOTE: De-Commenting it makes an opportunity ID not usable anymore
      // function: 'SMART',
    })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalAgileResponseSubject.next(response);
      });
  }

  public initializeFissoRequest() {
    this.approvalFissoResponseSubject.next({ status: 200 });
  }

  public checkFissoRequestContractId(id) {
    this.loadingSubject.next(true);
    this.checkFissoRequestStatus(id)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        if (!!response.result && response.result.prospectInfo) {
          if (response.result.prospectInfo.Alg_OTPCheck__c) {
            this.isOtpAgileRequest$.next(true);
          }
        } else {
          this.isOtpAgileRequest$.next(false);
        }
        this.fissoResponseSubject.next(response);
        this.approvalFissoResponseSubject.next(response);
      });
  }

  public saveFissoRequest(fisso: Fisso) {
    this.loadingSubject.next(true);
    this.saveNewFissoRequest({ ...fisso })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalFissoResponseSubject.next(response);
      });
  }

  public saveChangePlanRequestAndSendEmail(changePlan: CambioPiano) {
    this.loadingSubject.next(true);
    this.saveChangePlanRequest({ ...changePlan })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalFissoResponseSubject.next(response);
      });
  }

  public saveBillingFrequencyRequestAndSendEmail(billingFrequency: BillingFrequency) {
    this.loadingSubject.next(true);
    this.saveBillingFrequencyRequest({ ...billingFrequency })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.billingFrequencyResponseSubject.next(response);
      });
  }

  public saveDocsReceptionRequestAndSendEmail(docsReception: DocsReception) {
    this.loadingSubject.next(true);
    this.saveDocsReceptionRequest({ ...docsReception })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.docsReceptionResponseSubject.next(response);
      });
  }

  public saveSupplyInvoiceReceptionRequestAndSendEmail(supplyInvoiceReception: SupplyInvoiceReception) {
    this.loadingSubject.next(true);
    this.saveSupplyInvoiceReceptionRequest({ ...supplyInvoiceReception })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.supplyInvoiceReceptionResponseSubject.next(response);
      });
  }

  public savePaymentMethodRequestAndSendEmail(paymentMethod: PaymentMethod) {
    this.loadingSubject.next(true);
    this.savePaymentMethodRequest({ ...paymentMethod })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.paymentMethodResponseSubject.next(response);
      });
  }

  public saveAutoricaricaRequestAndSendEmail(autoricarica: Autoricarica) {
    this.loadingSubject.next(true);
    this.saveAutoricaricaRequest({ ...autoricarica })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.autoricaricaResponseSubject.next(response);
      });
  }

  public uploadAutoricaricaRequest(data) {
    this.loadingSubject.next(true);
    this.uploadAutoricaricaFile(data)
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.autoricaricaUploadDocumentsResponseSubject.next(response);
      });
  }

  public saveFeedback(approvalId, rating) {
    this.feedbackLoadingSubject.next(true);
    this.updateApproval(approvalId, { operatorRatingCode: rating })
      .pipe(
        tap(() => {
          this.feedbackLoadingSubject.next(false);
        })
      )
      .subscribe((response) => {
        this.approvalResponseSubject.next(response);
      });
  }

  public updatePrivacyConsents(consents: { valid: boolean; data: ApprovalPrivacyConsents }): void {
    this.privacyConsentsSubject.next(consents);
  }

  public updateGenericConsents(consents: { valid: boolean; data: ApprovalGenericConsents }): void {
    this.genericConsentsSubject.next(consents);
  }

  public updateGasPolicy(gasLinear) {
    this.gasLinearSubject$.next(gasLinear);
  }

  public updateGasInformative(gasLinear) {
    this.gasLinearInformative$.next(gasLinear);
  }

  public updateLightPolicy(lightLinear) {
    this.lightLinearSubject$.next(lightLinear);
  }

  public updateLightInformative(lightLinear) {
    this.lightLinearInformative$.next(lightLinear);
  }

  // delete attivazione from storage and model
  public deleteIstanza(data) {
    const endpoint = `${environment.api.basePath}/delete-istanza-switch-in/${data.id}`;
    return this.http
      .post(endpoint, data, {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json; charset=UTF-8',
        }),
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          result: !!response.body && response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            result: null,
          })
        )
      );
  }

  private transformApproval(approval): Approval {
    const { _id, operatorRatingCode, ...cleanedApproval } = approval || ({} as Approval);
    return {
      id: _id,
      operatorRatingCode: parseInt(operatorRatingCode, 10) || null,
      ...cleanedApproval,
    };
  }

  private transformLinear(request): ApprovalLinearResponse {
    const { _id, operatorRatingCode, ...cleanedApproval } = request || ({} as ApprovalLinearRequest);
    return {
      id: _id,
      operatorRatingCode: parseInt(operatorRatingCode, 10) || null,
      ...cleanedApproval,
    };
  }

  private getOpportunity(id: string): Observable<ApprovalResponse> {
    return this.http.get(`${this.opportunitiesEndpoint}/${id}`, { observe: 'response' }).pipe(
      map((response: HttpResponse<any>) => ({
        status: response.status,
        approval: this.transformApproval(response.body),
      })),
      catchError((response: HttpResponse<any>) =>
        Observable.of({
          status: response.status,
          approval: null,
        })
      )
    );
  }

  private getPromoNameOpportunity(id: string): Observable<any> {
    return this.http
      .get(`${this.promoNameEndpoint}/${id}`, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getLinearRequest(id: string, codice: string, tipo: string): Observable<any> {
    return this.http
      .get(`${this.linearRequest}/${id}/${codice}/${tipo}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getChangePlanRequest(id: string, codice: string, tipo: string): Observable<any> {
    return this.http
      .get(`${this.changePlanRequest}/${id}/${codice}/${tipo}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getBillingFrequency(id: string, codice: string, tipo: string): Observable<any> {
    return this.http
      .get(`${this.billingFrequencyRequest}/${id}/${codice}/${tipo}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getDocsReception(id: string): Observable<any> {
    return this.http
      .get(`${this.docsReceptionRequest}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          if (response.body.Alg_OTPCheck__c) {
            this.isOtpDocsReceptionRequest$.next(true);
          }
          return {
            status: response.status,
            request: response.body,
          };
        }),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getSupplyInvoiceReception(id: string): Observable<any> {
    return this.http
      .get(`${this.supplyInvoiceReceptionRequest}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          if (response.body.Alg_OTPCheck__c) {
            this.isOtpSupplyInvoiceReceptionRequest$.next(true);
          }
          return {
            status: response.status,
            request: response.body,
          };
        }),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getPaymentMethodRequest(id: string): Observable<any> {
    return this.http
      .get(`${this.paymentMethodRequest}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          if (response.body.Alg_OTPCheck__c) {
            this.isOtpPaymentMethodRequest$.next(true);
          }
          return {
            status: response.status,
            request: response.body,
          };
        }),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getAutoRicaricaInfos(data: any): Observable<any> {
    return this.http
      .post(`${this.autoRicaricaRequest}/`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getAutoRicaricaSuppliesInfo(supplies: any): Observable<any> {
    return this.http
      .post(`${this.autoRicaricaRequest}/supply-type`, supplies, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getAgileRequest(id: string): Observable<any> {
    return this.http
      .get(`${this.agileRequest}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private getOpportunityUnipol(id: string, unipol: boolean): Observable<ApprovalResponse> {
    return this.http
      .get(`${this.opportunitiesEndpoint}/${id}/${unipol}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          approval: this.transformApproval(response.body),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            approval: null,
          })
        )
      );
  }

  private saveOpportunity(id: string, data: any) {
    return this.http
      .post(`${this.opportunitiesEndpoint}/${id}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          approval: this.transformApproval(response.body),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            approval: null,
          })
        )
      );
  }

  private saveOpportunityUnipol(id: string, data: any) {
    return this.http
      .post(`${this.opportunitiesEndpoint}/unipol/${id}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          approval: this.transformApproval(response.body),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            approval: null,
          })
        )
      );
  }

  private saveNewLinearRequest(data: ApprovalLinearRequest) {
    return this.http
      .post(`${this.linearRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: true,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveNewAgileRequest(data: ApprovalAgileRequest) {
    return this.http
      .post(`${this.agileRequest}/${data.contractId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveNewFissoRequest(data) {
    return this.http
      .post(`${this.fissoRequest}/${data.ContractId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveChangePlanRequest(data) {
    return this.http
      .post(`${this.sendChangePlanRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveBillingFrequencyRequest(data) {
    return this.http
      .post(`${this.billingFrequencyRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveDocsReceptionRequest(data) {
    return this.http
      .post(`${this.confirmDocsReceptionRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveSupplyInvoiceReceptionRequest(data) {
    return this.http
      .post(`${this.confirmSupplyInvoiceReceptionRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private savePaymentMethodRequest(data) {
    return this.http
      .post(`${this.confirmPaymentMethodRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private saveAutoricaricaRequest(data) {
    return this.http
      .post(`${this.sendAutoricaricaRequest}/${data.requestId}`, data, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private uploadAutoricaricaFile(data) {
    return this.http
      .post(`${this.uploadAutoricaricaPDF}`, data, {
        observe: 'response',
        headers: new HttpHeaders({
          Accept: 'application/json',
        }),
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          request: this.transformLinear(response.body),
          isLinear: false,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            request: null,
          })
        )
      );
  }

  private checkFissoRequestStatus(id) {
    return this.http
      .get(`${this.fissoContractIdStatus}/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          result: response.body,
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            result: response.statusText,
          })
        )
      );
  }

  private updateApproval(id, data) {
    return this.http
      .put(`${this.approvalsEndpoint}/${id}`, data, {
        observe: 'response',
        headers: new HttpHeaders({
          'Content-Type': 'application/json; charset=UTF-8',
        }),
      })
      .pipe(
        map((response: HttpResponse<any>) => ({
          status: response.status,
          approval: !!response.body && this.transformApproval(response.body.approval),
        })),
        catchError((response: HttpResponse<any>) =>
          Observable.of({
            status: response.status,
            approval: null,
          })
        )
      );
  }
}
export { ApprovalFeedback };



// WEBPACK FOOTER //
// ./src/app/services/alg-consenso.service.ts